import type {
  GenerateLookalikesDto,
  LookalikePollResult,
  LookalikeView,
  LookalikeWeightUpdate,
} from '@repo/contracts';

import { apiFetch } from './client';

/**
 * A persona's lookalike population — see `@repo/persona-lookalikes` for the
 * schema/sampling split this is built on.
 *
 * Everything imported from `@repo/contracts` here is type-only, for the
 * reason `canvas-personas.ts` gives.
 */

export const lookalikeKeys = {
  all: ['persona-lookalikes'] as const,
  /** One persona's spec and materialised population. */
  forPersona: (personaId: string) => [...lookalikeKeys.all, personaId] as const,
};

/** `null` when nobody has generated a population for this persona yet. */
export function getLookalikes(
  personaId: string,
): Promise<LookalikeView | null> {
  return apiFetch<LookalikeView | null>(
    `/canvas/personas/${encodeURIComponent(personaId)}/lookalikes`,
  );
}

/** Writes (or rewrites) the spec: one model call, then a sample. */
export function generateLookalikes(
  personaId: string,
  dto: GenerateLookalikesDto,
): Promise<LookalikeView> {
  return apiFetch<LookalikeView>(
    `/canvas/personas/${encodeURIComponent(personaId)}/lookalikes`,
    { body: JSON.stringify(dto), method: 'POST' },
  );
}

/** Adjusts the sampling weights and returns the re-sampled population. No
 *  model call — see `PersonaLookalikesService.updateWeights`'s own doc. */
export function updateLookalikeWeights(
  personaId: string,
  updates: LookalikeWeightUpdate[],
): Promise<LookalikeView> {
  return apiFetch<LookalikeView>(
    `/canvas/personas/${encodeURIComponent(personaId)}/lookalikes/weights`,
    { body: JSON.stringify({ updates }), method: 'PATCH' },
  );
}

/** A yes/no question, fanned out to every lookalike via Jev. Nothing here is
 *  persisted — see `LookalikePollResult`'s own doc. */
export function pollLookalikes(
  personaId: string,
  question: string,
): Promise<LookalikePollResult> {
  return apiFetch<LookalikePollResult>(
    `/canvas/personas/${encodeURIComponent(personaId)}/lookalikes/poll`,
    { body: JSON.stringify({ question }), method: 'POST' },
  );
}
